import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Package,
  ShoppingCart,
  PackageOpen,
  Truck,
  AlertTriangle,
  RefreshCw,
  XCircle,
  Calendar,
  ExternalLink,
  User,
  Phone,
  MapPin,
  CheckCircle,
  DollarSign,
  List,
  PackageCheck,
  Instagram,
} from "lucide-react";
import { FaTiktok, FaWhatsapp } from "react-icons/fa";
import { cn } from "@/lib/utils";
import { useAuthContext } from "@/lib/AuthProvider";
import { usePaginatedOrders } from "@/hooks/usePaginatedOrders";
import { OrderStatus, cancelOrder } from "@/services/orderService";
import { fetchProductById } from "@/services/productService";
import ProductPagination from "@/components/ProductPagination";
import LoadingSpinner from "@/components/LoadingSpinner";
import AuthModalWrapper from "@/components/AuthModalWrapper";
import { useCart } from "@/context/CartContext";
import { Product } from "@/data/products";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getOrderHistorySEO, validateSEOData, generateKeywords } from "@/services/seoService";
import { fetchCarriers, Carrier, generateCarrierTrackingUrl } from "@/services/carrierService";

const OrderHistory = () => {
  const [activeTab, setActiveTab] = useState<OrderStatus | "all-orders">(
    "all-orders"
  );
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [isTabChanging, setIsTabChanging] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState<any>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const { user } = useAuthContext();
  const { addToCart } = useCart();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [totalNavHeight, setTotalNavHeight] = useState(112); // Default navbar height
  const [carriers, setCarriers] = useState<Carrier[]>([]);
  const [isWideViewport, setIsWideViewport] = useState(true);

  // Use the new paginated orders hook
  const {
    orders,
    loading,
    error,
    totalCount,
    currentPage,
    totalPages,
    fetchPage,
  } = usePaginatedOrders({
    userId: user?.uid || "",
    status: activeTab,
    pageSize: 10,
    enabled: !!user?.uid,
  });

  // Listen for navigation height updates from NavBar
  useEffect(() => {
    const handleNavHeightChange = (event: CustomEvent) => {
      setTotalNavHeight(event.detail.height);
    };

    // Listen for height updates from NavBar
    window.addEventListener(
      "navHeightUpdate",
      handleNavHeightChange as EventListener
    );

    return () => {
      window.removeEventListener(
        "navHeightUpdate",
        handleNavHeightChange as EventListener
      );
    };
  }, []);

  // Load carriers on mount
  useEffect(() => {
    fetchCarriers().then(setCarriers).catch(() => {});
  }, []);

  useEffect(() => {
    const handleViewportCheck = () => {
      if (typeof window === "undefined") return;
      const viewportWidth = window.innerWidth;
      // Only show Need Help card when wider than ~11" iPad width (~834px)
      setIsWideViewport(viewportWidth > 834);
    };

    handleViewportCheck();
    window.addEventListener("resize", handleViewportCheck);
    return () => window.removeEventListener("resize", handleViewportCheck);
  }, []);

  // Get carrier name from code
  const getCarrierName = (carrierCode: string): string => {
    const carrier = carriers.find(c => c.code === carrierCode);
    return carrier?.name || carrierCode;
  };

  // Generate tracking URL from carrier template
  const getTrackingUrl = (carrierCode: string, trackingNumber: string): string | null => {
    const carrier = carriers.find(c => c.code === carrierCode);
    return carrier && trackingNumber ? generateCarrierTrackingUrl(carrier, trackingNumber) : null;
  };

  // Handle tab change with loading state
  const handleTabChange = async (value: string) => {
    if (value === activeTab) return; // No change needed

    setIsTabChanging(true);
    setActiveTab(value as OrderStatus | "all-orders");

    // Small delay to show the loading state before data loads
    setTimeout(() => {
      setIsTabChanging(false);
    }, 300);
  };

  // Handle pagination
  const handlePageChange = async (page: number) => {
    await fetchPage(page);
  };

  // Open auth modal
  const openAuthModal = () => {
    setAuthModalOpen(true);
  };

  // Format date
  const formatDate = (dateString: string | Date | any) => {
    try {

      let date: Date;

      // Handle different date formats
      if (typeof dateString === "string") {
        date = new Date(dateString);
      } else if (dateString instanceof Date) {
        date = dateString;
      } else if (dateString && typeof dateString === "object") {
        // Handle Firestore Timestamp objects
        if (typeof (dateString as any).toDate === "function") {
          date = (dateString as any).toDate();
        } else if (typeof (dateString as any)._seconds === "number") {
          const seconds = (dateString as any)._seconds;
          const nanoseconds = (dateString as any)._nanoseconds || 0;
          date = new Date(seconds * 1000 + nanoseconds / 1000000);
        } else {
          throw new Error("Unrecognized date object format");
        }
      } else {
        throw new Error("Invalid date format");
      }

      // Check if the date is valid
      if (isNaN(date.getTime())) {
        throw new Error("Invalid date value");
      }

      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (error) {
      console.error("Date formatting error:", error, "Input:", dateString);
      return "Unknown date";
    }
  };

  // Format delivery date (date only, no time) - specific for estimated delivery dates
  const formatDeliveryDate = (dateString: string | Date | any) => {
    try {
      let date: Date;

      // Handle different date formats
      if (typeof dateString === "string") {
        date = new Date(dateString);
      } else if (dateString instanceof Date) {
        date = dateString;
      } else if (dateString && typeof dateString === "object") {
        // Handle Firestore Timestamp objects
        if (typeof (dateString as any).toDate === "function") {
          date = (dateString as any).toDate();
        } else if (typeof (dateString as any)._seconds === "number") {
          const seconds = (dateString as any)._seconds;
          const nanoseconds = (dateString as any)._nanoseconds || 0;
          date = new Date(seconds * 1000 + nanoseconds / 1000000);
        } else {
          throw new Error("Unrecognized date object format");
        }
      } else {
        throw new Error("Invalid date format");
      }

      // Check if the date is valid
      if (isNaN(date.getTime())) {
        throw new Error("Invalid date value");
      }

      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (error) {
      console.error("Date formatting error:", error, "Input:", dateString);
      return "Unknown date";
    }
  };

  // Helper function to get effective price (sale price if on sale, otherwise regular price)
  const getEffectivePrice = (item: any) => {
    if (item.onSale && item.onSalePrice && item.onSalePrice < item.price) {
      return item.onSalePrice;
    }
    return item.price;
  };

  // Helper function to check if an item is on sale
  const isItemOnSale = (item: any) => {
    return item.onSale && item.onSalePrice && item.onSalePrice < item.price;
  };

  // Currency conversion helpers using order's stored metadata
  const getOrderCurrency = (order: any) => {
    return order?.purchaseCurrency || 'USD';
  };

  const getOrderExchangeRates = (order: any) => {
    return order?.exchangeRateSnapshot || { USD: 1, CAD: 1.35, GBP: 0.8 };
  };

  // Convert USD price to order's display currency using stored exchange rate
  const getConvertedItemPriceFromOrder = (usdPrice: number, order: any) => {
    const currency = getOrderCurrency(order);
    const rates = getOrderExchangeRates(order);
    
    if (currency === 'USD') {
      return usdPrice;
    }
    
    const rate = rates[currency] || 1;
    return Math.ceil(usdPrice * rate);
  };

  // Format currency with order's currency symbol and code
  const formatOrderCurrency = (amount: number, order: any) => {
    const currency = getOrderCurrency(order);
    
    if (currency === 'USD') {
      return `$${amount.toFixed(2)} USD`;
    } else if (currency === 'CAD') {
      return `$${amount.toFixed(2)} CAD`;
    } else if (currency === 'GBP') {
      return `£${amount.toFixed(2)} GBP`;
    }
    
    return `$${amount.toFixed(2)}`;
  };

  // Calculate order total using per-item conversion for accuracy
  const getOrderTotal = (order: any) => {
    if (!order.items) return 0;
    
    // Calculate converted subtotal (per-item conversion)
    const convertedSubtotal = order.items.reduce((total: number, item: any) => {
      const unitPriceUsd = getEffectivePrice(item);
      const convertedUnitPrice = getConvertedItemPriceFromOrder(unitPriceUsd, order);
      return total + (convertedUnitPrice * item.quantity);
    }, 0);
    
    // Calculate converted shipping (individual conversion)
    const baseShippingCost = order?.shipping?.cost || 0;
    const convertedShipping = order?.discount?.type === 'free_shipping' 
      ? 0 
      : getConvertedItemPriceFromOrder(baseShippingCost, order);
    
    // Calculate converted priority fee (individual conversion)
    const convertedPriorityFee = getConvertedItemPriceFromOrder(order?.shipping?.priorityFee || 0, order);
    
    // Calculate converted discount (individual conversion)
    const convertedDiscount = order?.discount?.type !== 'free_shipping' 
      ? getConvertedItemPriceFromOrder(order?.discount?.amount || 0, order) 
      : 0;
    
    // Total calculation: subtotal + shipping + priority - discount
    return convertedSubtotal + convertedShipping + convertedPriorityFee - convertedDiscount;
  };

  // Map order status to display status
  const getDisplayStatus = (status: string) => {
    switch (status) {
      case "paid":
        return "processing";
      case "pending":
        return "processing";
      case "processed":
        return "processed";
      default:
        return status;
    }
  };


  // Handle Buy Again functionality
  const handleBuyAgain = async (order: any) => {
    try {
      // Import fetchProductById dynamically to avoid circular dependency issues
      const { fetchProductById } = await import("@/services/productService");

      const buyAgainItems = order.items.filter(
        (orderItem: any) => !orderItem.rushdTreat
      );

      if (buyAgainItems.length === 0) {
        toast({
          title: "No Items Available",
          description: "Rush'd treats aren't re-added automatically. Please add other items manually.",
        });
        return;
      }

      // Fetch current product data for each eligible item in the order
      const productPromises = buyAgainItems.map(async (orderItem: any) => {
        try {
          const currentProduct = await fetchProductById(orderItem.id);
          return {
            product: currentProduct,
            quantity: orderItem.quantity,
            originalName: orderItem.name, // Keep original name as fallback
          };
        } catch (error) {
          console.warn(
            `Failed to fetch current data for product ${orderItem.id}:`,
            error
          );
          return {
            product: null,
            quantity: orderItem.quantity,
            originalName: orderItem.name,
          };
        }
      });

      const productResults = await Promise.all(productPromises);

      let addedCount = 0;
      let skippedCount = 0;
      const skippedProducts: string[] = [];

      // Add available products to cart with current pricing
      for (const result of productResults) {
        if (result.product) {
          // Add current product with current pricing to cart
          addToCart(result.product, result.quantity);
          addedCount++;
        } else {
          // Product is no longer available
          skippedCount++;
          skippedProducts.push(result.originalName);
        }
      }

      // Show appropriate success/warning message
      if (addedCount > 0 && skippedCount === 0) {
        toast({
          title: "Items Added to Cart",
          description: `${addedCount} item(s) from your previous order have been added to your cart with current pricing.`,
        });
      } else if (addedCount > 0 && skippedCount > 0) {
        toast({
          title: "Some Items Added to Cart",
          description: `${addedCount} item(s) added to cart. ${skippedCount} item(s) are no longer available: ${skippedProducts.join(
            ", "
          )}.`,
          variant: "default",
        });
      } else {
        toast({
          title: "No Items Added",
          description:
            "Sorry, none of the items from this order are currently available.",
          variant: "destructive",
        });
        return; // Don't navigate to cart if no items were added
      }

      // Navigate to cart page
      navigate("/cart");
    } catch (error) {
      console.error("Error adding items to cart:", error);
      toast({
        title: "Error",
        description: "Failed to add items to cart. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Handle Cancel Order functionality
  const handleCancelOrder = async (order: any) => {
    setOrderToCancel(order);
    setCancelDialogOpen(true);
  };

  // Confirm and execute order cancellation
  const confirmCancelOrder = async () => {
    if (!orderToCancel) return;

    try {
      setIsCancelling(true);
      // Use the backend API to cancel the order
      await cancelOrder(orderToCancel.id);

      // Show success message
      toast({
        title: "Order Cancelled",
        description: `Order ${orderToCancel.id} has been successfully cancelled.`,
      });

      // Close dialog and reset state
      setCancelDialogOpen(false);
      setOrderToCancel(null);

      // Refresh the orders list to show updated status
      window.location.reload();
    } catch (error) {
      console.error("Error cancelling order:", error);
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to cancel order. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsCancelling(false);
    }
  };

  // Helper function to get refund message for cancelled orders
  const getRefundMessage = (order: any) => {
    if (!order || order.status !== "cancelled") return null;

    const paymentStatus = order.payment?.status;
    const refundProcessedAt = order.refund?.processedAt;

    // If payment status is 'succeeded' or 'refund_pending', show refund processing message
    if (paymentStatus === "succeeded" || paymentStatus === "refund_pending") {
      return {
        type: "processing",
        message:
          "Your refund is being processed and our team may reach out to you if necessary.",
      };
    }

    // If payment status is 'refunded', show refund completion message with processed date
    if (paymentStatus === "refunded") {
      let message = "Your refund has been processed and should appear on your statement within 5-10 business days from the refund date.";
      
      // If we have the refund processed date, include it in the message
      if (refundProcessedAt) {
        const processedDate = formatDate(refundProcessedAt);
        message = `Your refund was processed on ${processedDate} and should appear on your statement within 5-10 business days.`;
      }

      return {
        type: "completed",
        message: message,
        processedDate: refundProcessedAt ? formatDate(refundProcessedAt) : null,
      };
    }

    return null;
  };

  // Order Listing Section Component
  const OrderListingSection = () => {
    // Show tab changing loader
    if (isTabChanging) {
      return (
        <div className="h-[240px] flex items-center justify-center">
          <div className="text-center">
            <LoadingSpinner size="lg" />
            <p className="mt-4 text-gray-500">Loading orders...</p>
          </div>
        </div>
      );
    }

    // Show main loading state (initial load)
    if (loading && !isTabChanging) {
      return (
        <div className="h-[240px] flex items-center justify-center">
          <div className="text-center">
            <LoadingSpinner size="lg" />
            <p className="mt-4 text-gray-500">Loading your orders...</p>
          </div>
        </div>
      );
    }

    // Show error state
    if (error) {
      return (
        <div className="space-y-6">
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 px-8">
              {/* Error Icon Container */}
              <div className="flex justify-center mb-6">
                <div className="relative">
                  <div className="w-20 h-20 bg-gradient-to-br from-red-100 to-red-200 rounded-full flex items-center justify-center shadow-lg animate-pulse">
                    <AlertTriangle className="w-10 h-10 text-red-600" />
                  </div>
                  <div className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                    <XCircle className="w-3 h-3 text-white" />
                  </div>
                </div>
              </div>

              {/* Error Content */}
              <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 text-center w-full max-w-lg">
                <h2 className="text-2xl font-bold text-gray-900 mb-3">
                  Orders Load Error
                </h2>

                <p className="text-gray-600 mb-6 leading-relaxed">
                  We're having trouble loading your orders right now. This might
                  be a temporary problem with our servers.
                </p>

                {/* Error Details */}
                <div className="bg-gray-50 rounded-xl p-4 mb-6 border-l-4 border-red-400">
                  <p className="text-sm text-gray-700 font-mono break-words">
                    {error}
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  <Button
                    className="w-full bg-gradient-to-r from-jamaica-green to-green-600 hover:from-jamaica-green/90 hover:to-green-600/90 text-white font-semibold py-3 transition-all duration-300 transform hover:scale-105 shadow-lg"
                    onClick={() => window.location.reload()}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Try Again
                  </Button>

                  <Button
                    variant="outline"
                    className="w-full border-gray-300 text-gray-700 hover:bg-gray-50 transition-all duration-300"
                    onClick={() => navigate("/products")}
                  >
                    Continue Shopping
                  </Button>
                </div>
              </div>

              {/* Help Text */}
              <div className="mt-6 text-center">
                <p className="text-sm text-gray-500">
                  If the problem persists, please{" "}
                  <a
                    href="mailto:support@jampackd.store?subject=Orders%20Load%20Error%20Report&body=Hello%20JamPack'd%20Support%20Team,%0D%0A%0D%0AI%20encountered%20an%20error%20while%20trying%20to%20view%20my%20orders%20on%20your%20website:%0D%0A%0D%0AError%20Details:%0D%0A%0D%0APlease%20help%20me%20resolve%20this%20issue.%0D%0A%0D%0AThank%20you!"
                    className="text-jamaica-green hover:text-jamaica-green/80 font-medium transition-colors duration-200"
                  >
                    contact our support team
                  </a>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Show empty state
    if (orders.length === 0) {
      return (
        <div className="space-y-6">
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <ShoppingCart className="h-12 w-12 text-gray-400 mb-4" />
              <p className="text-xl font-medium text-gray-900">
                No orders found
              </p>
              <p className="text-gray-500 mt-2">
                You don't have any orders in this category yet.
              </p>
              <Button
                className="mt-6 bg-jamaica-green hover:bg-jamaica-green/90 text-white"
                onClick={() => navigate("/products")}
              >
                Continue Shopping
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Show orders with smooth fade-in animation
    return (
      <div className="space-y-6 animate-in fade-in duration-300">
        {orders.map((order) => {
          const displayStatus = getDisplayStatus(order.status);
          return (
            <Card
              key={order.id}
              className="overflow-hidden transform transition-all hover:shadow-lg"
            >
              <CardHeader className="bg-gray-50 border-b border-gray-100 py-3 md:py-4">
                <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-3 lg:gap-0">
                  {/* Order Info & Icon */}
                  <div className="flex items-center gap-3 md:gap-4">
                    <div className="flex-shrink-0">
                      {displayStatus === "delivered" ? (
                        <Package className="h-6 w-6 md:h-8 md:w-8 text-jamaica-green" />
                      ) : displayStatus === "shipped" ? (
                        <Truck className="h-6 w-6 md:h-8 md:w-8 text-blue-500" />
                      ) : displayStatus === "processed" ? (
                        <Package className="h-6 w-6 md:h-8 md:w-8 text-purple-500" />
                      ) : displayStatus === "cancelled" ? (
                        <XCircle className="h-6 w-6 md:h-8 md:w-8 text-red-500" />
                      ) : (
                        <PackageOpen className="h-6 w-6 md:h-8 md:w-8 text-jamaica-yellow" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <CardTitle className="text-base md:text-lg">
                        Order
                      </CardTitle>
                      <div className="text-xs text-gray-600 mt-0.5 break-all leading-tight">
                        #{order.id}
                      </div>
                      <CardDescription className="text-xs md:text-sm">
                        {formatDate(order.createdAt)}
                      </CardDescription>
                    </div>
                  </div>

                  {/* Mobile: Stack vertically, Desktop: Row layout */}
                  <div className="flex flex-col sm:flex-row lg:items-center gap-2 sm:gap-3">
                    {/* Priority Processing Indicator */}
                    {order.shipping?.priorityProcessing && (
                      <div className="flex items-center gap-1.5 bg-gradient-to-r from-jamaica-green/20 via-emerald-300/30 to-jamaica-green/20 px-2 py-1 rounded-lg border border-jamaica-green/30 shadow-sm self-start sm:self-auto">
                        <div className="relative">
                          <div className="w-2.5 h-2.5 md:w-3 md:h-3 bg-jamaica-green rounded-full animate-pulse"></div>
                          <div className="absolute -inset-0.5 w-3.5 h-3.5 md:w-4 md:h-4 bg-jamaica-green rounded-full animate-ping opacity-30"></div>
                        </div>
                        <span className="text-xs font-semibold text-jamaica-green uppercase tracking-wide">
                          ⚡ Priority
                        </span>
                      </div>
                    )}

                    {/* Status & Discount Badges Row */}
                    <div className="flex items-center justify-between sm:justify-start sm:gap-3">
                      <div className="flex items-center gap-2">
                        <Badge
                          className={cn(
                            "capitalize px-2 py-0.5 text-xs md:px-2.5",
                            displayStatus === "delivered"
                              ? "bg-green-100 text-green-800 hover:bg-green-100"
                              : displayStatus === "shipped"
                              ? "bg-blue-100 text-blue-800 hover:bg-blue-100"
                              : displayStatus === "processed"
                              ? "bg-purple-100 text-purple-800 hover:bg-purple-100"
                              : displayStatus === "cancelled"
                              ? "bg-red-100 text-red-800 hover:bg-red-100"
                              : "bg-amber-100 text-amber-800 hover:bg-amber-100"
                          )}
                        >
                          {displayStatus}
                        </Badge>

                        {order.discount && (
                          <div className="flex items-center gap-1 px-2 py-0.5 md:py-1 bg-green-100 rounded-full border border-green-200">
                            <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                            <span className="text-xs font-semibold text-green-700">
                              {order.discount.type === "free_shipping"
                                ? "🚚 Free Ship"
                                : `🎉 ${order.discount.code}`}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Price */}
                      <div className="flex flex-col items-end">
                        <span className="font-semibold text-base md:text-lg">
                          {formatOrderCurrency(getOrderTotal(order), order)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="py-3 md:py-4">
                <div className="space-y-2 md:space-y-3">
                  {order.items.slice(0, 2).map((item, index) => (
                    <div
                      key={item.id || index}
                      className="flex items-center gap-2 md:gap-3"
                    >
                      <div className="flex-shrink-0">
                        <img
                          src={item.image || "/placeholder-product.png"}
                          alt={item.name}
                          className="w-10 h-10 md:w-12 md:h-12 object-cover rounded-md border border-gray-200"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = "/placeholder-product.png";
                          }}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-900 font-medium truncate text-sm md:text-base leading-tight">
                          {item.name}
                        </p>
                        
                        {/* Brand and Weight Info for Order History Items */}
                        <div className="flex items-center gap-1 mt-1 mb-1 flex-wrap">
                          {(item as any).brand && (
                            <span className="inline-flex items-center text-xs font-medium text-jamaica-green bg-jamaica-green/10 px-1.5 py-0.5 rounded-full border border-jamaica-green/20">
                              {(item as any).brand}
                            </span>
                          )}
                          {(item as any).netWeight && (item as any).weightUnit && (
                            <span className="inline-flex items-center text-xs text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded-full">
                              {(item as any).netWeight}{(item as any).weightUnit}
                            </span>
                          )}
                        </div>
                        
                        <p className="text-xs md:text-sm text-gray-500 mt-0.5">
                          Qty: {item.quantity}
                        </p>
                        {isItemOnSale(item) && (
                          <div className="flex items-center gap-1 mt-1">
                            <span className="text-xs bg-jamaica-black text-jamaica-yellow px-1 py-0.5 rounded font-medium">
                              SALE
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        <div className="text-right">
                          {isItemOnSale(item) && (
                            <p className="text-xs text-gray-500 line-through">
                              {formatOrderCurrency(getConvertedItemPriceFromOrder(item.price, order) * item.quantity, order)}
                            </p>
                          )}
                          <p
                            className={`font-medium text-sm md:text-base ${
                              isItemOnSale(item)
                                ? "text-jamaica-yellow"
                                : "text-gray-700"
                            }`}
                          >
                            {formatOrderCurrency(getConvertedItemPriceFromOrder(getEffectivePrice(item), order) * item.quantity, order)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                  {order.items.length > 2 && (
                    <p className="text-xs md:text-sm text-gray-500 ml-12 md:ml-15 mt-1">
                      + {order.items.length - 2} more item(s)
                    </p>
                  )}

                  {order.tracking && (
                    <div className="mt-3 md:mt-4 pt-3 md:pt-4 border-t border-gray-100">
                      {/* Header Section with Gradient */}
                      <div className="relative overflow-hidden rounded-lg bg-gradient-to-r from-jamaica-green/5 via-jamaica-yellow/5 to-jamaica-green/10 border border-jamaica-green/20 mb-3 md:mb-4">
                        <div className="absolute inset-0 bg-gradient-to-r from-jamaica-green/5 to-transparent"></div>
                        <div className="relative px-3 md:px-4 py-2.5 md:py-3">
                          <div className="flex items-center gap-2 md:gap-3">
                            <div className="w-7 h-7 md:w-8 md:h-8 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-lg shadow-sm flex items-center justify-center flex-shrink-0">
                              <Truck className="w-3.5 h-3.5 md:w-4 md:h-4 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="text-xs md:text-sm font-semibold text-jamaica-black">
                                Package Tracking
                              </h4>
                              <p className="text-xs text-jamaica-black/60 hidden sm:block">
                                Track your order progress
                              </p>
                            </div>
                            {order.tracking.trackingNumber && getTrackingUrl(order.tracking.carrier, order.tracking.trackingNumber) && (
                              <Button
                                size="sm"
                                className="bg-jamaica-green hover:bg-jamaica-green/90 text-white text-xs px-2 md:px-3 py-1 md:py-1.5 h-auto shadow-sm flex-shrink-0"
                                onClick={() =>
                                  window.open(getTrackingUrl(order.tracking!.carrier, order.tracking!.trackingNumber)!, "_blank")
                                }
                              >
                                <span className="hidden sm:inline">
                                  Track Live
                                </span>
                                <span className="sm:hidden">Track</span>
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Tracking Details Grid */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 md:gap-3 mb-2 md:mb-3">
                        {/* Carrier */}
                        <div className="group bg-gradient-to-br from-white to-gray-50/50 border border-jamaica-green/10 rounded-lg p-2.5 md:p-3 hover:shadow-sm transition-all duration-200">
                          <div className="flex items-center gap-2 md:gap-3">
                            <div className="w-5 h-5 md:w-6 md:h-6 bg-gradient-to-br from-jamaica-green/10 to-jamaica-green/5 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:from-jamaica-green/20 group-hover:to-jamaica-green/10 transition-colors duration-200">
                              <Truck className="w-3 h-3 md:w-3.5 md:h-3.5 text-jamaica-green" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-medium text-jamaica-black/50 uppercase tracking-wide">
                                Carrier
                              </p>
                              <p className="text-xs md:text-sm font-semibold text-jamaica-black truncate leading-tight">
                                {getCarrierName(order.tracking.carrier)}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Tracking Number */}
                        <div className="group bg-gradient-to-br from-white to-gray-50/50 border border-jamaica-green/10 rounded-lg p-2.5 md:p-3 hover:shadow-sm transition-all duration-200">
                          <div className="flex items-center gap-2 md:gap-3">
                            <div className="w-5 h-5 md:w-6 md:h-6 bg-gradient-to-br from-jamaica-green/10 to-jamaica-green/5 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:from-jamaica-green/20 group-hover:to-jamaica-green/10 transition-colors duration-200">
                              <Package className="w-3 h-3 md:w-3.5 md:h-3.5 text-jamaica-green" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-medium text-jamaica-black/50 uppercase tracking-wide">
                                Tracking #
                              </p>
                              <p className="text-xs md:text-sm font-mono font-semibold text-jamaica-black truncate leading-tight">
                                {order.tracking.trackingNumber}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Estimated Delivery - Enhanced */}
                      {order.tracking.estimatedDelivery && (
                        <div className="bg-gradient-to-r from-jamaica-green/5 via-jamaica-yellow/5 to-jamaica-green/5 border border-jamaica-green/20 rounded-lg p-2.5 md:p-3 shadow-sm">
                          <div className="flex items-center gap-2 md:gap-3">
                            <div className="w-5 h-5 md:w-6 md:h-6 bg-gradient-to-br from-jamaica-green/20 to-jamaica-green/10 rounded-lg flex items-center justify-center flex-shrink-0">
                              <Package className="w-3 h-3 md:w-3.5 md:h-3.5 text-jamaica-green" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-medium text-jamaica-black/50 uppercase tracking-wide">
                                Estimated Delivery
                              </p>
                              <p className="text-xs md:text-sm font-semibold text-jamaica-black leading-tight">
                                {typeof order.tracking.estimatedDelivery ===
                                "string"
                                  ? order.tracking.estimatedDelivery
                                  : order.tracking.estimatedDelivery
                                  ? formatDeliveryDate(
                                      order.tracking.estimatedDelivery
                                    )
                                  : "N/A"}
                              </p>
                            </div>
                            <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-jamaica-green rounded-full animate-pulse"></div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Refund Message for Cancelled Orders */}
                  {displayStatus === "cancelled" &&
                    (() => {
                      const refundMessage = getRefundMessage(order);
                      if (refundMessage) {
                        return (
                          <div className="mt-4 pt-4 border-t border-gray-100">
                            <div
                              className={`p-3 rounded-lg border ${
                                refundMessage.type === "processing"
                                  ? "bg-yellow-50 border-yellow-200"
                                  : "bg-green-50 border-green-200"
                              }`}
                            >
                              <div className="flex items-start gap-2">
                                {refundMessage.type === "processing" ? (
                                  <DollarSign className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                                ) : (
                                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                )}
                                <div>
                                  <div>
                                    <p
                                      className={`text-sm font-medium ${
                                        refundMessage.type === "processing"
                                          ? "text-yellow-800"
                                          : "text-green-800"
                                      }`}
                                    >
                                      {refundMessage.type === "processing"
                                        ? "Refund Processing"
                                        : "Refund Processed"}
                                    </p>
                                    {refundMessage.type === "completed" && refundMessage.processedDate && (
                                      <p
                                        className="text-xs mt-0.5 text-green-700 font-medium"
                                      >
                                        Processed: {refundMessage.processedDate}
                                      </p>
                                    )}
                                  </div>
                                  <p
                                    className={`text-xs mt-1 ${
                                      refundMessage.type === "processing"
                                        ? "text-yellow-600"
                                        : "text-green-600"
                                    }`}
                                  >
                                    {refundMessage.message}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })()}
                </div>
              </CardContent>

              <CardFooter className="flex justify-between py-3 bg-gray-50 border-t border-gray-100">
                <Button
                  variant="outline"
                  className="text-sm"
                  onClick={() => navigate(`/order-details/${order.id}`)}
                >
                  View Details
                </Button>
                {displayStatus === "delivered" && (
                  <Button
                    variant="outline"
                    className="text-sm"
                    onClick={() => handleBuyAgain(order)}
                  >
                    Buy Again
                  </Button>
                )}
                {displayStatus === "processing" && (
                  <Button
                    variant="outline"
                    className="text-sm text-red-600 border-red-300 hover:bg-red-50 hover:text-red-700"
                    onClick={() => handleCancelOrder(order)}
                  >
                    Cancel Order
                  </Button>
                )}
              </CardFooter>
            </Card>
          );
        })}

        {/* Pagination */}
        {totalPages > 1 && (
          <ProductPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            className="mt-8"
          />
        )}
      </div>
    );
  };

  // If not authenticated, show login prompt
  if (!user) {
    return (
      <div
        className="flex flex-col"
        style={{ minHeight: `calc(100vh - ${totalNavHeight}px)` }}
      >
        <main className="flex-grow relative bg-gradient-to-br from-gray-50 via-white to-gray-100 overflow-hidden">
          {/* Animated Background Elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {/* Floating Geometric Shapes */}
            <div className="absolute top-20 left-20 w-16 h-16 bg-gradient-to-br from-jamaica-green/10 to-jamaica-green/5 rounded-2xl transform rotate-45 animate-pulse delay-300"></div>
            <div className="absolute top-40 right-32 w-12 h-12 bg-jamaica-yellow/15 rounded-full animate-bounce delay-500"></div>
            <div className="absolute bottom-32 left-1/4 w-20 h-20 bg-gradient-to-br from-jamaica-yellow/8 to-jamaica-green/8 rounded-3xl transform rotate-12 animate-ping delay-700"></div>
            <div className="absolute bottom-20 right-20 w-14 h-14 bg-jamaica-green/12 rounded-lg transform -rotate-12 animate-pulse delay-900"></div>
            
            {/* Diagonal Lines Pattern */}
            <div className="absolute inset-0 opacity-5">
              <div className="absolute inset-0" style={{
                backgroundImage: `
                  linear-gradient(45deg, rgba(0, 116, 47, 0.1) 1px, transparent 1px),
                  linear-gradient(-45deg, rgba(255, 201, 1, 0.1) 1px, transparent 1px)
                `,
                backgroundSize: '80px 80px, 120px 120px',
                backgroundPosition: '0 0, 40px 40px'
              }}></div>
            </div>
            
            {/* Gradient Blobs */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-l from-jamaica-green/3 to-jamaica-yellow/3 rounded-full mix-blend-multiply filter blur-3xl animate-pulse delay-1000"></div>
            <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-r from-jamaica-yellow/4 to-jamaica-green/3 rounded-full mix-blend-multiply filter blur-2xl animate-pulse delay-1500"></div>
          </div>
          
          <div className="relative container mx-auto py-16 px-4 sm:px-6 lg:px-8 flex items-center justify-center min-h-full">
            <div className="text-center py-16 relative backdrop-blur-sm bg-white/80 rounded-2xl shadow-xl border border-white/40 hover:shadow-2xl transition-all duration-500">
              {/* Animated Border Accent */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-jamaica-green via-jamaica-yellow to-jamaica-green opacity-60 rounded-t-2xl"></div>
              
              <div className="mb-6">
                <div className="relative inline-block">
                  <div className="bg-gradient-to-br from-jamaica-green/10 to-jamaica-yellow/10 rounded-full p-6 inline-block shadow-lg">
                    <Package className="h-16 w-16 text-jamaica-green" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-jamaica-yellow rounded-full flex items-center justify-center shadow-md">
                    <span className="text-xs font-bold text-white">!</span>
                  </div>
                </div>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Sign in to view your orders
              </h1>
              <p className="text-gray-600 mb-8 max-w-md mx-auto">
                You need to be signed in to view your order history and track your purchases.
              </p>
              <Button
                className="bg-gradient-to-r from-jamaica-green to-jamaica-green/90 text-white hover:from-jamaica-green/90 hover:to-jamaica-green shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 px-8 py-3 text-lg font-semibold rounded-xl"
                onClick={openAuthModal}
              >
                <User className="h-5 w-5 mr-2" />
                Sign In
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Generate SEO data for Order History page
  const seoData = validateSEOData(getOrderHistorySEO());

  return (
    <>
      <Helmet>
        {/* Basic Meta Tags */}
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        <meta name="author" content={seoData.author} />
        
        {/* Open Graph Tags for Social Sharing */}
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:image" content={seoData.image} />
        <meta property="og:url" content={seoData.url} />
        <meta property="og:type" content={seoData.type} />
        <meta property="og:site_name" content={seoData.siteName} />
        <meta property="og:locale" content={seoData.locale} />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoData.title} />
        <meta name="twitter:description" content={seoData.description} />
        <meta name="twitter:image" content={seoData.image} />
        
        {/* Additional SEO Tags */}
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href={seoData.url} />
      </Helmet>
      
      <div
        className="flex flex-col"
        style={{ minHeight: `calc(100vh - ${totalNavHeight}px)` }}
      >
      {/* Auth Modal */}
      <AuthModalWrapper
        isOpen={authModalOpen}
        onOpenChange={setAuthModalOpen}
        defaultTab="login"
      />

      <main className="flex-grow relative bg-gradient-to-br from-gray-50 via-white to-gray-100">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Floating Geometric Shapes */}
          <div className="absolute top-20 left-20 w-16 h-16 bg-gradient-to-br from-jamaica-green/10 to-jamaica-green/5 rounded-2xl transform rotate-45 animate-pulse delay-300"></div>
          <div className="absolute top-40 right-32 w-12 h-12 bg-jamaica-yellow/15 rounded-full animate-bounce delay-500"></div>
          <div className="absolute bottom-32 left-1/4 w-20 h-20 bg-gradient-to-br from-jamaica-yellow/8 to-jamaica-green/8 rounded-3xl transform rotate-12 animate-ping delay-700"></div>
          <div className="absolute bottom-20 right-20 w-14 h-14 bg-jamaica-green/12 rounded-lg transform -rotate-12 animate-pulse delay-900"></div>
          
          {/* Diagonal Lines Pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{
              backgroundImage: `
                linear-gradient(45deg, rgba(0, 116, 47, 0.1) 1px, transparent 1px),
                linear-gradient(-45deg, rgba(255, 201, 1, 0.1) 1px, transparent 1px)
              `,
              backgroundSize: '80px 80px, 120px 120px',
              backgroundPosition: '0 0, 40px 40px'
            }}></div>
          </div>
          
          {/* Gradient Blobs */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-l from-jamaica-green/3 to-jamaica-yellow/3 rounded-full mix-blend-multiply filter blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-r from-jamaica-yellow/4 to-jamaica-green/3 rounded-full mix-blend-multiply filter blur-2xl animate-pulse delay-1500"></div>
        </div>
        
        <div className="relative container mx-auto py-10 px-4 sm:px-6 lg:px-8">
          {/* Enhanced Hero Section */}
          <div className="mb-12 text-center">
            <div className="inline-flex items-center gap-3 mb-6 sm:mb-4">
              <div className="relative">
                <div className="w-10 h-10 sm:w-14 sm:h-14 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-2xl flex items-center justify-center shadow-xl transform rotate-6 sm:hover:-rotate-6 transition-transform duration-500">
                  <Package className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
                </div>
                <div className="absolute -top-2 -right-2 w-5 h-5 sm:w-6 sm:h-6 bg-jamaica-yellow rounded-full flex items-center justify-center animate-bounce">
                  <span className="text-[10px] sm:text-xs font-bold text-white">📦</span>
                </div>
              </div>
              <div className="bg-gradient-to-r from-jamaica-green/10 to-jamaica-yellow/10 px-3 py-1.5 sm:px-5 sm:py-2 rounded-2xl border border-jamaica-green/20 shadow-lg">
                <span className="text-xs sm:text-sm font-bold text-jamaica-black">
                  Order Management
                </span>
              </div>
            </div>

            <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-2 sm:mb-4 leading-tight">
              <span className="bg-gradient-to-r from-jamaica-green via-jamaica-yellow to-jamaica-green bg-clip-text text-transparent p-1">
                Order History
              </span>
            </h1>
            <p className="hidden sm:block text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              View and track your orders
            </p>
            <p className="sm:hidden text-sm text-gray-600 max-w-md mx-auto leading-relaxed">
              View and track your orders.
            </p>
          </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        {/* Sidebar with track package feature */}
        {isWideViewport && (
        <aside className="md:col-span-3 space-y-6 hidden md:block">
          <Card 
            className="sticky z-20 bg-white shadow-lg" 
            style={{ top: `${totalNavHeight + 24}px` }}
          >
            <CardHeader>
              <CardTitle className="text-xl font-semibold">
                Need Help?
              </CardTitle>
              <CardDescription>We're here to assist you</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-sm font-medium text-gray-500">
                  Email Support
                </p>
                <p className="text-sm text-gray-700">support@jampackd.store</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Socials</p>
                <div className="flex items-center gap-3 text-gray-600">
                  <a
                    href="https://instagram.com/jampackdcarepackages"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-jamaica-green transition-colors"
                  >
                    <Instagram className="h-4 w-4" />
                  </a>
                  <a
                    href="https://tiktok.com/@jampackdcarepackages"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-jamaica-green transition-colors"
                  >
                    <FaTiktok className="h-4 w-4" />
                  </a>
                  <a
                    href="https://wa.me/18763276630?text=Hi%20Paul%21%20I%27d%20appreciate%20your%20help%20with%20something"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-jamaica-green transition-colors"
                  >
                    <FaWhatsapp className="h-4 w-4" />
                  </a>
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Phone</p>
                <p className="text-sm text-gray-700">(876) 327-6630</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Hours</p>
                <p className="text-sm text-gray-700">Mon-Fri, 9am-5pm EST</p>
              </div>
            </CardContent>
          </Card>
        </aside>
        )}

        {/* Main content area */}
        <div className={cn(
          "space-y-6",
          isWideViewport ? "md:col-span-9" : "md:col-span-12"
        )}>
          <Tabs
            value={activeTab}
            onValueChange={handleTabChange}
            className="w-full"
          >
            <TabsList className="grid w-full md:w-auto md:inline-flex grid-cols-6 mb-8 bg-muted">
              <TabsTrigger
                value="all-orders"
                className="flex items-center gap-2 px-4"
              >
                <List className="h-4 w-4" />
                <span className="hidden md:inline">All Orders</span>
              </TabsTrigger>
              <TabsTrigger
                value="paid"
                className="flex items-center gap-2 px-4"
              >
                <PackageOpen className="h-4 w-4" />
                <span className="hidden md:inline">Processing</span>
              </TabsTrigger>
              <TabsTrigger
                value="processed"
                className="flex items-center gap-2 px-4"
              >
                <Package className="h-4 w-4" />
                <span className="hidden md:inline">Processed</span>
              </TabsTrigger>
              <TabsTrigger
                value="shipped"
                className="flex items-center gap-2 px-4"
              >
                <Truck className="h-4 w-4" />
                <span className="hidden md:inline">Shipped</span>
              </TabsTrigger>
              <TabsTrigger
                value="delivered"
                className="flex items-center gap-2 px-4"
              >
                <PackageCheck className="h-4 w-4" />
                <span className="hidden md:inline">Delivered</span>
              </TabsTrigger>
              <TabsTrigger
                value="cancelled"
                className="flex items-center gap-2 px-4"
              >
                <XCircle className="h-4 w-4" />
                <span className="hidden md:inline">Cancelled</span>
              </TabsTrigger>
            </TabsList>

            {/* Tab content */}
            <TabsContent value={activeTab} className="space-y-6 mt-0">
              <OrderListingSection />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Cancel Order Confirmation Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Cancel Order</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel this order? This action cannot be
              undone.
            </DialogDescription>
          </DialogHeader>

          {orderToCancel && (
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <div className="font-medium text-sm">Order</div>
              <div className="text-xs font-mono text-gray-600 mt-1 break-all">
                {orderToCancel.id}
              </div>
              <div className="text-sm text-gray-600 mt-1">
                {orderToCancel.items.length} item(s) • {formatOrderCurrency(getOrderTotal(orderToCancel), orderToCancel)}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCancelDialogOpen(false)}
              disabled={isCancelling}
            >
              Keep Order
            </Button>
            <Button
              variant="destructive"
              onClick={confirmCancelOrder}
              disabled={isCancelling}
            >
              {isCancelling ? "Cancelling..." : "Cancel Order"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
        </div>
      </main>
    </div>
    </>
  );
};

export default OrderHistory;
